import { fetchJSON } from './http';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface Shop {
  id: string;
  name: string;
  address: any;
  phone: string;
  email: string;
  website?: string;
  openingHours: any;
  socialMedia: any;
  onlineBookingEnabled: boolean;
}

export interface Service {
  id: string;
  name: string;
  description: string;
  category: string;
  subcategory?: string;
  basePrice: number;
  priceRange: any;
  estimatedDuration: number;
  onlineBookingEnabled: boolean;
  depositRequired: boolean;
  depositType?: 'FIXED' | 'PERCENTAGE';
  depositAmount?: number;
  maxAdvanceBookingDays?: number;
  planningRecommendation?: string | null;
  images: string[];
}

export interface Practitioner {
  id: string;
  firstName: string;
  lastName: string;
  artistName?: string;
  specialties: any;
  bio: string;
  experience: number;
  portfolio: any;
  rating: number;
  reviewCount: number;
  color?: string;
  onlineBookingEnabled: boolean;
}

export interface Slot {
  start: string;
  end: string;
  slotSizeMin: number;
  slotCount: number;
  practitionerId?: string;
  isAvailable: boolean;
  reason?: string;
}

export interface ReferenceImage {
  imageId: string;
  fileName: string;
  url: string;
  thumbnailUrl: string;
  fileSize: number;
  mimeType: string;
  tempStorage?: {
    path: string;
    clientId: string;
    rightsConfirmed: boolean;
    usageRights: 'REFERENCE_ONLY' | 'OWNED_BY_CUSTOMER' | 'LICENSED' | 'PUBLIC_DOMAIN';
    gdprConsentGiven: boolean;
  };
}

export interface OnlineBookingRequest {
  clientId?: string;
  registrationData?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth: string;
    password?: string;
  };
  shopId: string;
  practitionerId?: string;
  serviceId: string;
  appointmentDate: string;
  selectedTimeSlot: string;
  project?: {
    description: string;
    motif?: string;
    placement: {
      area: string;
      side?: string;
    };
    size: {
      width: number;
      height: number;
      unit: 'cm' | 'inch';
    };
    style: 'TRADITIONAL' | 'REALISM' | 'WATERCOLOR' | 'GEOMETRIC' | 'MINIMALIST' | 'BLACKWORK' | 'DOTWORK' | 'TRIBAL' | 'JAPANESE' | 'BIOMECHANICAL';
    colorType: 'BLACK_GREY' | 'COLOR' | 'MIXED';
    notesToArtist?: string;
  };
  referenceImageIds?: string[];
  referenceImages?: Array<{
    imageId: string;
    fileName: string;
    url: string;
    thumbnailUrl?: string;
    fileSize: number;
    mimeType: string;
    tempStorage?: {
      path: string;
      clientId: string;
      rightsConfirmed: boolean;
      usageRights: 'REFERENCE_ONLY' | 'OWNED_BY_CUSTOMER' | 'LICENSED' | 'PUBLIC_DOMAIN';
      gdprConsentGiven: boolean;
    };
  }>;
  paymentMethod: 'CARD' | 'DIGITAL_WALLET';
  termsAccepted: boolean;
  cancellationPolicyAccepted: boolean;
  imageRightsAccepted: boolean;
}

export interface OnlineBookingResponse {
  success: boolean;
  data: {
    appointmentId: string;
    projectId: string | null;
    appointmentType?: 'CONSULTATION' | 'TATTOO_SESSION';
    status: 'PENDING_PAYMENT' | 'CONFIRMED';
    depositRequired: boolean;
    depositAmount?: number | null;
    qrCodeId?: string | null;
    qrCodeImage?: string;
    checkInCode?: string;
    message: string;
  };
}

export interface DepositPaymentRequest {
  appointmentId: string;
  paymentMethod: 'CARD' | 'DIGITAL_WALLET';
  transactionId?: string;
  gatewayResponse?: any;
}

export interface DepositPaymentResponse {
  success: boolean;
  data: {
    paymentId: string;
    appointmentId: string;
    status: 'CONFIRMED';
    qrCodeId: string;
    qrCodeImage: string;
    checkInCode?: string;
    message: string;
  };
}

class OnlineBookingApi {
  private baseUrl = `${API_BASE_URL}/api/online-booking`;

  /**
   * Get available shops for online booking
   */
  async getShops(): Promise<{ success: boolean; data: { shops: Shop[] } }> {
    return fetchJSON<{ success: boolean; data: { shops: Shop[] } }>(
      `${this.baseUrl}/shops`,
      { method: 'GET' }
    );
  }

  /**
   * Get available services for online booking
   */
  async getServices(shopId: string, category?: string): Promise<{ success: boolean; data: { services: Service[] } }> {
    const params = new URLSearchParams({ shopId });
    if (category) {
      params.append('category', category);
    }
    
    return fetchJSON<{ success: boolean; data: { services: Service[] } }>(
      `${this.baseUrl}/services?${params.toString()}`,
      { method: 'GET' }
    );
  }

  /**
   * Get available practitioners for a shop/service
   */
  async getPractitioners(shopId: string, serviceId?: string): Promise<{ success: boolean; data: { practitioners: Practitioner[] } }> {
    const params = new URLSearchParams({ shopId });
    if (serviceId) {
      params.append('serviceId', serviceId);
    }
    
    return fetchJSON<{ success: boolean; data: { practitioners: Practitioner[] } }>(
      `${this.baseUrl}/practitioners?${params.toString()}`,
      { method: 'GET' }
    );
  }

  /**
   * Get available time slots
   */
  async getAvailability(
    shopId: string,
    serviceId: string,
    from: string,
    to: string,
    practitionerId?: string
  ): Promise<{ success: boolean; data: { slots: Slot[]; shopTimezone: string; slotSize: number } }> {
    const params = new URLSearchParams({
      shopId,
      serviceId,
      from,
      to
    });
    
    if (practitionerId) {
      params.append('practitionerId', practitionerId);
    }
    
    return fetchJSON<{ success: boolean; data: { slots: Slot[]; shopTimezone: string; slotSize: number } }>(
      `${this.baseUrl}/availability?${params.toString()}`,
      { method: 'GET' }
    );
  }

  /**
   * Upload reference image
   */
  async uploadImage(
    file: File,
    rightsConfirmed: boolean,
    usageRights: 'REFERENCE_ONLY' | 'OWNED_BY_CUSTOMER' | 'LICENSED' | 'PUBLIC_DOMAIN',
    gdprConsentGiven: boolean
  ): Promise<{ success: boolean; data: ReferenceImage }> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('rightsConfirmed', rightsConfirmed.toString());
    formData.append('usageRights', usageRights);
    formData.append('gdprConsentGiven', gdprConsentGiven.toString());

    const token = localStorage.getItem('client_token');
    const response = await fetch(`${this.baseUrl}/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to upload image');
    }

    return data;
  }

  /**
   * Create online booking
   */
  async createBooking(request: OnlineBookingRequest): Promise<OnlineBookingResponse> {
    return fetchJSON<OnlineBookingResponse>(
      `${this.baseUrl}/book`,
      {
        method: 'POST',
        body: request as unknown as Record<string, unknown>,
        requireAuth: true as boolean
      }
    );
  }

  /**
   * Process deposit payment (legacy / fallback)
   */
  async payDeposit(request: DepositPaymentRequest): Promise<DepositPaymentResponse> {
    return fetchJSON<DepositPaymentResponse>(
      `${this.baseUrl}/pay-deposit`,
      {
        method: 'POST',
        body: request as unknown as Record<string, unknown>,
        requireAuth: true as boolean
      }
    );
  }

  /**
   * Create a hosted checkout session for deposit payment.
   */
  async createCheckoutSession(appointmentId: string): Promise<{ success: boolean; data: { provider?: 'STRIPE' | 'VIVA'; checkoutUrl: string; sessionId: string } }> {
    return fetchJSON<{ success: boolean; data: { provider?: 'STRIPE' | 'VIVA'; checkoutUrl: string; sessionId: string } }>(
      `${this.baseUrl}/create-checkout-session`,
      {
        method: 'POST',
        body: { appointmentId } as unknown as Record<string, unknown>,
        requireAuth: false,
      }
    );
  }

  /**
   * Verify payment status for an appointment / hosted provider session.
   */
  async verifyPayment(params: { sessionId?: string; appointmentId?: string; provider?: string; transactionRef?: string; status?: string; orderCode?: string; vivaTransactionId?: string }): Promise<{ success: boolean; data: { provider?: string; paymentStatus: string; appointmentId: string; appointment: any } }> {
    const query = new URLSearchParams();
    if (params.sessionId) query.set('session_id', params.sessionId);
    if (params.appointmentId) query.set('appointment_id', params.appointmentId);
    if (params.provider) query.set('provider', params.provider);
    if (params.transactionRef) query.set('transaction_ref', params.transactionRef);
    if (params.status) query.set('status', params.status);
    if (params.orderCode) query.set('s', params.orderCode);
    if (params.vivaTransactionId) query.set('t', params.vivaTransactionId);
    return fetchJSON(
      `${this.baseUrl}/verify-payment?${query.toString()}`,
      { method: 'GET', requireAuth: false }
    );
  }
}

export const onlineBookingApi = new OnlineBookingApi();

